import { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { RoleRow } from '../../core/api/types';
import type { MatrixResource, ResourceInput } from '../../core/authorization/api';
import { ACTION_ORDER, type ActionName } from '../../core/authorization/permissionMatrix';

interface Props {
  open: boolean;
  /** Resource id when editing; null when adding. */
  editingId: string | null;
  initial: ResourceInput | null;
  productName: string;
  roots: MatrixResource[];
  roles: RoleRow[];
  saving: boolean;
  error: string;
  onClose: () => void;
  onSubmit: (input: ResourceInput) => void;
}

const slug = (s: string) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/**
 * "+ Add resource" popup (platform operators only: the catalog is shared by every organization).
 * Three sections: where it appears, access, initial access. The code is derived from the label
 * until the operator edits it; the parent's scope is offered as the default scope.
 */
export function ResourceDialog({ open, editingId, initial, productName, roots, roles, saving, error, onClose, onSubmit }: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState<ResourceInput | null>(initial);
  const [codeTouched, setCodeTouched] = useState(false);
  const [scopeTouched, setScopeTouched] = useState(false);
  useEffect(() => {
    if (open) { setForm(initial); setCodeTouched(!!editingId); setScopeTouched(!!editingId); }
  }, [open, initial, editingId]);
  if (!form) return null;

  const prefix = form.productCode.toLowerCase();
  const set = (patch: Partial<ResourceInput>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const actionLabel = (a: ActionName) => t(`permissions.actions.${a}`, { defaultValue: a[0].toUpperCase() + a.slice(1) });
  const toggleAction = (a: ActionName) => {
    if (a === 'read') return;
    const on = form.supported.includes(a);
    let next = on ? form.supported.filter((x) => x !== a) : [...form.supported, a];
    if (a === 'execute' && !on) next = [...ACTION_ORDER]; // Execute = everything
    if (a !== 'execute' && on) next = next.filter((x) => x !== 'execute');
    set({ supported: ACTION_ORDER.filter((x) => x === 'read' || next.includes(x)) });
  };
  const urlError = form.visibleInMenu && !/^\/[\w\-/:.]*$/.test(form.url) ? t('permissions.urlError', { defaultValue: 'Use a path that starts with "/".' }) : '';
  const scopeError = !new RegExp(`^(${prefix}|platform)\\.[a-z0-9-]+$`).test(form.scopeBase)
    ? t('permissions.scopeError', { defaultValue: 'Use "{{prefix}}.<area>", e.g. {{prefix}}.orders', prefix }) : '';
  const codeError = !new RegExp(`^${prefix}\\.[a-z0-9-]+(\\.[a-z0-9-]+)*$`).test(form.code)
    ? t('permissions.codeError', { defaultValue: 'Lowercase, starting with "{{prefix}}."', prefix }) : '';
  const readAlsoError = form.readAlso.filter(Boolean).find((a) => !new RegExp(`^(${prefix}|platform)\\.[a-z0-9-]+$`).test(a))
    ? t('permissions.readAlsoError', { defaultValue: 'Each area looks like "{{prefix}}.<area>" or "platform.<area>".', prefix }) : '';
  const valid = form.label.trim() && !urlError && !scopeError && !codeError && !readAlsoError;
  // Errors are shown once the operator has started filling the form, not on an empty form.
  const started = !!form.label.trim() || !!editingId;
  const shownScope = form.scopeBase.endsWith('.') ? `${prefix}.<area>` : form.scopeBase;

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} fullWidth maxWidth="md">
      <DialogTitle>
        {editingId ? t('permissions.editResource', { defaultValue: 'Edit resource' }) : t('permissions.addResource', { defaultValue: 'Add resource' })}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <div className="perm-sect">{t('permissions.whereItAppears', { defaultValue: 'Where it appears' })}</div>
        <div className="perm-grid2">
          <TextField label={t('permissions.product', { defaultValue: 'Product' })} value={productName} disabled />
          <TextField
            select
            label={t('permissions.parentMenu', { defaultValue: 'Parent menu' })}
            value={form.parentId ?? ''}
            helperText={t('permissions.parentHint', { defaultValue: 'Navigation has two levels: root menu → submenu.' })}
            onChange={(e) => {
              const parent = roots.find((r) => r.resourceId === e.target.value);
              set({ parentId: e.target.value || null, ...(!scopeTouched && parent?.scopeBase ? { scopeBase: parent.scopeBase } : {}) });
            }}
          >
            <MenuItem value="">{t('permissions.rootMenu', { defaultValue: '— Root menu —' })}</MenuItem>
            {roots.filter((r) => r.resourceId !== editingId).map((r) => <MenuItem key={r.resourceId} value={r.resourceId}>{r.title || r.label}</MenuItem>)}
          </TextField>
          <TextField
            autoFocus
            label={t('permissions.label', { defaultValue: 'Label (menu text / translation key)' })}
            value={form.label}
            onChange={(e) => {
              const label = e.target.value;
              set({ label, title: form.title && form.title !== form.label ? form.title : label,
                ...(!codeTouched ? { code: `${prefix}.${slug(label)}` } : {}) });
            }}
          />
          <TextField label={t('permissions.titleField', { defaultValue: 'Title' })} value={form.title} onChange={(e) => set({ title: e.target.value })} />
          <TextField label="URL" value={form.url} error={started && !!urlError} helperText={(started && urlError) || ' '} placeholder="/reservations/calendar"
            inputProps={{ style: { fontFamily: 'ui-monospace, monospace' } }} onChange={(e) => set({ url: e.target.value })} />
          <TextField label={t('permissions.icon', { defaultValue: 'Icon' })} value={form.icon} placeholder="event" onChange={(e) => set({ icon: e.target.value })} />
          <TextField type="number" label={t('permissions.sortOrder', { defaultValue: 'Sort order' })} value={form.sortOrder}
            onChange={(e) => set({ sortOrder: Number(e.target.value) })} />
          <TextField label={t('permissions.code', { defaultValue: 'Code' })} value={form.code} disabled={!!editingId} error={started && !editingId && !!codeError}
            helperText={(started && !editingId && codeError) || t('permissions.codeHint', { defaultValue: 'Permanent; products check grants by it.' })}
            inputProps={{ style: { fontFamily: 'ui-monospace, monospace' } }}
            onChange={(e) => { setCodeTouched(true); set({ code: e.target.value }); }} />
        </div>

        <div className="perm-sect">{t('permissions.accessSection', { defaultValue: 'Access' })}</div>
        <label className="perm-switch">
          <input type="checkbox" checked={form.visibleInMenu} onChange={(e) => set({ visibleInMenu: e.target.checked })} />
          <span className="perm-sw" aria-hidden="true" />
          <span>
            <b>{t('permissions.visibleInMenu', { defaultValue: 'Visible in menu' })}</b>
            <small>{t('permissions.visibleHint', { defaultValue: 'Visible menus are controlled by roles.' })}</small>
          </span>
        </label>
        {!form.visibleInMenu && (
          <Alert severity="warning" sx={{ my: 1 }}>
            {t('permissions.openWarning', { defaultValue: 'Hidden resources are open to every member of the organization. Give it its own scope, not one used by a visible menu.' })}
          </Alert>
        )}
        <div className="perm-grid2" style={{ marginTop: 12 }}>
          <TextField label={t('permissions.scope', { defaultValue: 'Scope' })} value={form.scopeBase} error={started && !!scopeError}
            helperText={(started && scopeError) || t('permissions.scopeHint', { defaultValue: 'Token permissions = scope + action.' })}
            inputProps={{ style: { fontFamily: 'ui-monospace, monospace' } }}
            onChange={(e) => { setScopeTouched(true); set({ scopeBase: e.target.value }); }} />
          <div>
            <div className="perm-field-label">{t('permissions.supportedActions', { defaultValue: 'Supported actions' })}</div>
            <div className="perm-chips">
              {ACTION_ORDER.map((a) => (
                <button key={a} type="button" aria-pressed={form.supported.includes(a)} disabled={a === 'read'}
                  className={`perm-chip ${form.supported.includes(a) ? 'on' : ''}`} onClick={() => toggleAction(a)}>{actionLabel(a)}</button>
              ))}
            </div>
            <small className="perm-hint">{t('permissions.supportedHint', { defaultValue: 'Read is always supported. Execute = everything + special commands.' })}</small>
          </div>
          <TextField className="perm-span2" label={t('permissions.readAlso', { defaultValue: 'Also reads on load (areas)' })}
            value={form.readAlso.join(' ')} error={started && !!readAlsoError}
            helperText={(started && readAlsoError) || t('permissions.readAlsoHint', { defaultValue: 'Other areas this page loads data from, separated by spaces, e.g. {{prefix}}.catalog {{prefix}}.dining. Read on this page grants read on these too (never write, never their menus).', prefix })}
            inputProps={{ style: { fontFamily: 'ui-monospace, monospace' } }}
            onChange={(e) => set({ readAlso: e.target.value.split(/[\s,;]+/).filter((x, i, all) => x.length > 0 || i === all.length - 1) })} />
          <TextField className="perm-span2" label={t('permissions.description', { defaultValue: 'Description' })} value={form.description}
            onChange={(e) => set({ description: e.target.value })} />
        </div>

        {!editingId && (
          <>
            <div className="perm-sect">{t('permissions.initialAccess', { defaultValue: 'Initial access' })}</div>
            <div className="perm-field-label">{t('permissions.grantToRoles', { defaultValue: 'Give full access to roles' })}</div>
            <div className="perm-chips">
              {roles.filter((r) => !r.isSystem).map((r) => r.isFullAccess ? (
                <span key={r.id} className="perm-chip on muted" title={t('permissions.fullIncluded', { defaultValue: 'Full-access roles always include new resources' })}>
                  {r.name} · {t('permissions.included', { defaultValue: 'included' })}
                </span>
              ) : (
                <button key={r.id} type="button" aria-pressed={form.grantToRoleIds.includes(r.id)}
                  className={`perm-chip ${form.grantToRoleIds.includes(r.id) ? 'on' : ''}`}
                  onClick={() => set({ grantToRoleIds: form.grantToRoleIds.includes(r.id)
                    ? form.grantToRoleIds.filter((x) => x !== r.id) : [...form.grantToRoleIds, r.id] })}>{r.name}</button>
              ))}
            </div>
          </>
        )}
        <div className="perm-preview">
          <div>{t('permissions.code', { defaultValue: 'Code' })} <b className="mono">{form.code}</b> · {form.visibleInMenu
            ? t('permissions.controlledByRoles', { defaultValue: 'Controlled by roles' })
            : <b>{t('permissions.openToEveryone', { defaultValue: 'Open to everyone' })}</b>}</div>
          <div className="mono">{form.supported.map((a) => `${shownScope}.${a}`).join('  ')}</div>
        </div>
      </DialogContent>
      <DialogActions>
        <span className="perm-hint" style={{ marginRight: 'auto', paddingLeft: 16 }}>
          {t('permissions.operatorsOnly', { defaultValue: 'Only platform operators can add resources (shared by every organization).' })}
        </span>
        <Button disabled={saving} onClick={onClose}>{t('permissions.cancel', { defaultValue: 'Cancel' })}</Button>
        <Button variant="contained" disabled={saving || !valid} onClick={() => onSubmit(form)}>
          {editingId ? t('permissions.saveResource', { defaultValue: 'Save resource' }) : t('permissions.addResource', { defaultValue: 'Add resource' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
